"use client";

import Image from "next/image";
import { useMemo } from "react";

import { FadeInIntersection } from "@/common/components";
import {
  Carousel,
  CarouselContent,
  CarouselDotButton,
  CarouselDotNavigation,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from "@/common/components/carousel";
import { SanitizedContent } from "@/common/components-2/sanitized-content";
import { Section } from "@/common/components-2/section";
import { SectionHeading } from "@/common/components-2/section-heading";
import { useTranslations } from "@/services/i18n";

const carouselButtonClass = "cursor-pointer border-none bg-white text-base text-foreground max-md:hidden";

export function ExpertsSection() {
  const tUi = useTranslations("ui");
  const t = useTranslations("pages.consultations.experts");

  const items = useMemo(
    () => [
      {
        degree: t("items.1.degree"),
        name: t.rich("items.1.name") as string,
        description: t("items.1.description"),
        image: {
          url: "https://assets-cdn.upacjenta.pl/7cd3091a-975e-4329-8ccb-9cc404de49a2.png",
          title: "",
          width: 0,
          height: 0,
        },
      },
      {
        degree: t("items.2.degree"),
        name: t.rich("items.2.name") as string,
        description: t("items.2.description"),
        image: {
          url: "https://assets-cdn.upacjenta.pl/b1795240-365d-4efe-beb0-455e7ad40cf2.png",
          title: "",
          width: 0,
          height: 0,
        },
      },
      {
        degree: t("items.4.degree"),
        name: t.rich("items.4.name") as string,
        description: t("items.4.description"),
        image: {
          url: "https://assets-cdn.upacjenta.pl/c6265e21-6951-4ba6-a33f-f59cc9197d4f.png",
          title: "",
          width: 0,
          height: 0,
        },
      },
      {
        degree: t("items.5.degree"),
        name: t.rich("items.5.name") as string,
        description: t("items.5.description"),
        image: {
          url: "https://assets-cdn.upacjenta.pl/9d04b415-c8ce-4962-bc0b-72d9b963e95a.png",
          title: "",
          width: 0,
          height: 0,
        },
      },
      {
        degree: t("items.6.degree"),
        name: t.rich("items.6.name") as string,
        description: t("items.6.description"),
        image: {
          url: "https://assets-cdn.upacjenta.pl/c386bacd-9595-4a8e-84a7-afe13ceebfe7.png",
          title: "",
          width: 0,
          height: 0,
        },
      },
      {
        degree: t("items.7.degree"),
        name: t.rich("items.7.name") as string,
        description: t("items.7.description"),
        image: {
          url: "https://assets-cdn.upacjenta.pl/fa731dd6-fcac-453d-a391-d4d0632bdf19.png",
          title: "",
          width: 0,
          height: 0,
        },
      },
    ],
    [t],
  );
  return (
    <FadeInIntersection rootMargin="0px 0px -300px 0px" delay={100} asChild animation="fadeUp">
      <Section classes={{ section: "py-10 md:py-14 lg:py-16" }}>
        <SectionHeading
          heading={t.rich("heading")}
          description={t("description")}
          classes={{ description: "text-center" }}
        />
        <Carousel opts={{ align: "end" }} className="mt-10 md:px-10">
          <CarouselContent>
            {items.map(({ degree, name, description, image }, index) => (
              <CarouselItem
                key={description}
                className="max-w-[312px] animate-fill-forwards opacity-0 group-[.animate-intersected-in]/intersected:animate-fade-right md:max-w-[284px]"
                style={{ animationDelay: `${index * 150}ms` }}
              >
                <div className="h-full rounded-2xl border border-foreground px-6 py-8">
                  <div className="flex items-center gap-5">
                    <Image src={image.url} alt={image.title} width={56} height={56} />
                    <div>
                      <p className="text-sm">{degree}</p>
                      <SanitizedContent as="p" content={name} className="mt-1 font-semibold text-lg" />
                    </div>
                  </div>
                  <p className="mt-8 text-base">{description}</p>
                </div>
              </CarouselItem>
            ))}
          </CarouselContent>
          <CarouselPrevious className={carouselButtonClass} aria-label={tUi("carousel.previous")} />
          <CarouselNext className={carouselButtonClass} aria-label={tUi("carousel.next")} />
          <CarouselDotNavigation
            items={(selectedIndex) =>
              items.map(({ name }, index) => (
                <CarouselDotButton
                  className={index === selectedIndex ? "bg-dodger-blue-700" : "bg-muted-foreground"}
                  key={name}
                  index={index}
                  active={index === selectedIndex}
                  aria-label={`${index + 1}`}
                />
              ))
            }
            className="mt-8 md:hidden"
          />
        </Carousel>
      </Section>
    </FadeInIntersection>
  );
}
